import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["action", "autoComplete", "children", "noValidate", "onKeyDown", "onSubmit"];
import { forwardRef, useCallback, useEffect, useRef, useMemo } from 'react';
import devLogger from 'react-utils/devLogger';
import invariant from 'react-utils/invariant';
import { syntheticEvent } from '../../utils/SyntheticEvent';
import styled from 'foundations-theming/utils';
import { useMapTokenPropsToCSS } from '../../../hooks/useMapTokenPropsToCSS';
import { filterProps } from '../../../utils/filterProps';
import { usePanelContext } from '../../context/PanelContext';
import { deepMerge } from '../../utils/deepMerge';
import { useDialogContext } from '../Dialog/DialogContext';
import { useFloatingFormContext } from '../FloatingForm/FloatingFormProvider';
import { useModalPanelContext } from '../ModalPanel/ModalPanelProvider';
import { jsx as _jsx } from "react/jsx-runtime";
let hasPingedNewRelic = false;
export const StyledForm = styled.form.withConfig({
  displayName: "Form__StyledForm"
})(({
  $cssStyles,
  $styleOverrides,
  $isInDialogOrModal,
  $isInPanel
}) => {
  return deepMerge(Object.assign({}, $isInDialogOrModal && {
    display: 'flex',
    flexDirection: 'column',
    flexGrow: 1
  }, $isInPanel && {
    display: 'flex',
    flex: '1 1 100%',
    flexDirection: 'column',
    maxHeight: '100%'
  }, $cssStyles), $styleOverrides);
});
export const Form = /*#__PURE__*/forwardRef((_ref, ref) => {
  let {
      action,
      autoComplete,
      children,
      noValidate,
      onKeyDown,
      onSubmit
    } = _ref,
    rest = _objectWithoutPropertiesLoose(_ref, _excluded);
  const internalFormRef = useRef(undefined);
  const panelContext = usePanelContext();
  const dialogContext = useDialogContext();
  const {
    inModal,
    inPanel
  } = panelContext || {};
  const {
    inDialog
  } = dialogContext || {};
  useEffect(() => {
    if (!internalFormRef.current) return;
    const childForms = internalFormRef.current.querySelectorAll('form');
    const nestedFormsExist = childForms.length > 0;
    if (nestedFormsExist) {
      if (window.newrelic && !hasPingedNewRelic) {
        window.newrelic.addPageAction('componentWithNestedForm', {
          componentName: 'Form'
        });
        hasPingedNewRelic = true;
      }
      if (process.env.NODE_ENV !== 'production') {
        devLogger.warn({
          message: `A \`<form />\` was rendered within a \`Form\`. Please refactor.`,
          url: 'https://git.hubteam.com/HubSpot/UIComponents/issues/9507',
          key: 'Form-nested'
        });
        invariant(nestedFormsExist, 'A form cannot be nested in a form.');
      }
    }
  }, [internalFormRef]);
  const {
    domProps,
    tokenProps
  } = filterProps(rest);
  const cssStyles = useMapTokenPropsToCSS(tokenProps);
  const floatingFormContext = useFloatingFormContext();
  const modalPanelContext = useModalPanelContext();
  const sharedStyleOverrides = useMemo(() => {
    var _floatingFormContext$, _modalPanelContext$fo;
    return deepMerge((_floatingFormContext$ = floatingFormContext === null || floatingFormContext === void 0 ? void 0 : floatingFormContext.formStylesOverrides) !== null && _floatingFormContext$ !== void 0 ? _floatingFormContext$ : {}, (_modalPanelContext$fo = modalPanelContext === null || modalPanelContext === void 0 ? void 0 : modalPanelContext.formStylesOverrides) !== null && _modalPanelContext$fo !== void 0 ? _modalPanelContext$fo : {});
  }, [floatingFormContext === null || floatingFormContext === void 0 ? void 0 : floatingFormContext.formStylesOverrides, modalPanelContext === null || modalPanelContext === void 0 ? void 0 : modalPanelContext.formStylesOverrides]);
  const getRef = useCallback(node => {
    internalFormRef.current = node;
    if (typeof ref === 'function') {
      ref(node);
    } else if (ref) {
      ref.current = node;
    }
  }, [ref]);
  return /*#__PURE__*/_jsx(StyledForm, Object.assign({
    ref: getRef,
    action: action,
    $cssStyles: cssStyles,
    $styleOverrides: sharedStyleOverrides,
    $isInDialogOrModal: inDialog || inModal,
    $isInPanel: inPanel,
    onKeyDown: evt => {
      // Submit on Cmd+Enter or Ctrl+Enter (avoiding double-submit from an <input>: #4578)
      const inputFocused = document.activeElement instanceof HTMLInputElement;
      if (evt.key === 'Enter') {
        if (evt.metaKey || evt.ctrlKey && !inputFocused) {
          // @ts-expect-error 'SyntheticEventClass<null>' is not assignable to parameter of type 'FormEvent<HTMLFormElement>'
          if (onSubmit) onSubmit(syntheticEvent(null));
        }
      }
      if (onKeyDown) onKeyDown(evt);
    },
    onSubmit: !action && !onSubmit ? evt => evt && evt.preventDefault() : onSubmit,
    noValidate: noValidate,
    autoComplete: autoComplete
  }, domProps, {
    children: children
  }));
});
Form.displayName = 'Form';